import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, IconButton, InputAdornment, LinearProgress, Stack, Typography, Chip } from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle, Cancel } from '@mui/icons-material';
import { useChangeUserPasswordMutation } from '../../../../hooks/mutations/useUserMutation';
import { User } from '../../../../services/apiSLTP/users';
import CustomTextInput from '../../../CustomTextInput';
import ModalBody from '../../ModalBody';
import { formatName, capitalizeFirstLetter } from '../../../../utils/capitalize';

interface ModalProps {
    user: User | null;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

type PasswordStrength = 'weak' | 'medium' | 'strong';

const MIN_PASSWORD_LENGTH = 5;

const UpdateUserPasswordModal: React. FC<ModalProps> = ({ user, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [newPassword, setNewPassword] = useState('');
    const [newPasswordSecond, setNewPasswordSecond] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [touched, setTouched] = useState({ password: false, confirm: false });

    const { mutate: mutatePassword, isLoading } = useChangeUserPasswordMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const handleSubmit = () => {
        mutatePassword({ userId: user?._id || '', body: { newPassword } });
    };

    const handleClose = () => {
        setNewPassword('');
        setNewPasswordSecond('');
        setTouched({ password: false, confirm: false });
        setShowPassword(false);
        setShowPasswordConfirm(false);
        setShowEditModal(false);
    };

    const calculatePasswordStrength = (password: string): PasswordStrength => {
        let strength = 0;
        if (password.length >= MIN_PASSWORD_LENGTH) strength++;
        if (password.length >= 12) strength++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
        if (/\d/.test(password)) strength++;
        if (/[^a-zA-Z\d]/.test(password)) strength++;

        if (strength <= 2) return 'weak';
        if (strength <= 4) return 'medium';
        return 'strong';
    };

    const passwordStrength = newPassword ? calculatePasswordStrength(newPassword) : null;

    const getStrengthColor = (strength: PasswordStrength | null) => {
        if (!strength) return 'grey';
        if (strength === 'weak') return 'error';
        if (strength === 'medium') return 'warning';
        return 'success';
    };

    const getStrengthValue = (strength: PasswordStrength | null) => {
        if (!strength) return 0;
        if (strength === 'weak') return 33;
        if (strength === 'medium') return 66;
        return 100;
    };

    const validations = {
        minLength: newPassword.length >= MIN_PASSWORD_LENGTH,
        hasUpperCase: /[A-Z]/.test(newPassword),
        hasLowerCase: /[a-z]/.test(newPassword),
        hasNumber: /\d/.test(newPassword),
        hasSpecialChar: /[^a-zA-Z\d]/.test(newPassword),
        passwordsMatch: newPassword === newPasswordSecond && newPasswordSecond.length > 0,
    };

    const isFormValid = () => {
        return (
            validations.minLength &&
            validations.hasUpperCase &&
            validations.hasLowerCase &&
            validations. hasNumber &&
            validations. hasSpecialChar &&
            validations.passwordsMatch
        );
    };

    const ValidationItem: React.FC<{ valid: boolean; label: string }> = ({ valid, label }) => (
        <Stack direction="row" spacing={1} alignItems="center">
            {valid ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" color="disabled" />}
            <Typography variant="caption" color={valid ? 'success. main' : 'text.secondary'}>
                {label}
            </Typography>
        </Stack>
    );

    const passwordError =
        touched.password && newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH
            ? capitalizeFirstLetter(t('passwordTooShort'))
            : undefined;

    const confirmError =
        touched.confirm && newPasswordSecond.length > 0 && newPassword !== newPasswordSecond
            ? capitalizeFirstLetter(t('passwordsDontMatch'))
            : undefined;

    return (
        <ModalBody
            title={t('user')}
            subTitle={t('password')}
            description={formatName(user?.firstName, user?.lastName)}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            disabledOk={!isFormValid() || isLoading}
            handleClose={handleClose}
            handleOk={handleSubmit}
        >
            <Stack spacing={3}>
                <Alert severity="info">{t('adminPasswordChangeInfo')}</Alert>

                <Box>
                    <CustomTextInput
                        value={newPassword}
                        label={capitalizeFirstLetter(t('newPassword') || 'Nouveau mot de passe')}
                        onChange={setNewPassword}
                        onBlur={() => setTouched({ ... touched, password: true })}
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        errorText={passwordError}
                        required
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton onClick={() => setShowPassword(! showPassword)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                    />

                    {newPassword.length > 0 && (
                        <Box sx={{ mt: 1.5, px: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                <Typography variant="caption" color="text. secondary">
                                    {capitalizeFirstLetter(t('passwordStrength'))}:
                                </Typography>
                                <Chip
                                    label={capitalizeFirstLetter(t(passwordStrength || '') || passwordStrength || '')}
                                    size="small"
                                    color={getStrengthColor(passwordStrength) as any}
                                    sx={{ height: 20 }}
                                />
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={getStrengthValue(passwordStrength)}
                                color={getStrengthColor(passwordStrength) as any}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>
                    )}
                </Box>

                <CustomTextInput
                    value={newPasswordSecond}
                    label={capitalizeFirstLetter(t('confirmNewPassword'))}
                    onChange={setNewPasswordSecond}
                    onBlur={() => setTouched({ ...touched, confirm: true })}
                    type={showPasswordConfirm ? 'text' : 'password'}
                    autoComplete="new-password"
                    errorText={confirmError}
                    required
                    endAdornment={
                        <InputAdornment position="end">
                            <IconButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} edge="end">
                                {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }
                />

                <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {capitalizeFirstLetter(t('passwordRequirements'))}:
                    </Typography>
                    <Stack spacing={0.5}>
                        <ValidationItem valid={validations.minLength} label={capitalizeFirstLetter(t('minLengthX', { length: MIN_PASSWORD_LENGTH }))} />
                        <ValidationItem valid={validations.hasUpperCase} label={capitalizeFirstLetter(t('hasUpperCase'))} />
                        <ValidationItem valid={validations.hasLowerCase} label={capitalizeFirstLetter(t('hasLowerCase'))} />
                        <ValidationItem valid={validations.hasNumber} label={capitalizeFirstLetter(t('hasNumber'))} />
                        <ValidationItem valid={validations.hasSpecialChar} label={capitalizeFirstLetter(t('hasSpecialChar'))} />
                        {newPasswordSecond.length > 0 && (
                            <ValidationItem valid={validations. passwordsMatch} label={capitalizeFirstLetter(t('passwordsMatch'))} />
                        )}
                    </Stack>
                </Box>
            </Stack>
        </ModalBody>
    );
};

export default UpdateUserPasswordModal;